import { useState, useEffect } from 'react';
import { useFile, useUI } from '../../context/AppContext.jsx';
import { useRobotConnection } from '../../hooks/useRobotConnection.js';

export default function UploadModal() {
  const { currentFileData, currentFileName, parser } = useFile();
  const { showUploadModal, setShowUploadModal } = useUI();
  const { uploadToRobot, uploadToRobotFTP } = useRobotConnection();
  const [filename, setFilename] = useState('');
  const [host, setHost] = useState('192.168.1.99');
  const [username, setUsername] = useState('root');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (showUploadModal && currentFileName) {
      setFilename(currentFileName);
    }
  }, [showUploadModal, currentFileName]);

  const handleUpload = async () => {
    if (!filename || !host || !username || !password) {
      setStatus('Please fill in all fields');
      return;
    }

    if (!filename.endsWith('.urp')) {
      setStatus('Filename must end with .urp');
      return;
    }

    if (!currentFileData) {
      setStatus('No file loaded to upload');
      return;
    }

    setIsUploading(true);
    setStatus('Uploading...');
    try {
      const xmlString = parser.getXMLString();
      // Convert XML string to Uint8Array and compress
      const textEncoder = new TextEncoder();
      const xmlBytes = textEncoder.encode(xmlString);
      const pako = (await import('pako')).default;
      const gzippedData = pako.gzip(xmlBytes);

      await uploadToRobot(host, username, password, filename, gzippedData);
      setShowUploadModal(false);
      setStatus('');
    } catch (error) {
      setStatus(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadFTP = async () => {
    if (!filename) {
      setStatus('Please enter a filename');
      return;
    }

    if (!filename.endsWith('.urp')) {
      setStatus('Filename must end with .urp');
      return;
    }

    if (!currentFileData) {
      setStatus('No file loaded to upload');
      return;
    }

    setIsUploading(true);
    setStatus('Uploading via FTP...');
    try {
      const xmlString = parser.getXMLString();
      // Convert XML string to Uint8Array and compress
      const textEncoder = new TextEncoder();
      const xmlBytes = textEncoder.encode(xmlString);
      const pako = (await import('pako')).default;
      const gzippedData = pako.gzip(xmlBytes);

      await uploadToRobotFTP(filename, gzippedData);
      setShowUploadModal(false);
      setStatus('');
    } catch (error) {
      setStatus(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  if (!showUploadModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-bg-secondary rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Upload to Robot</h2>
          <button
            onClick={() => setShowUploadModal(false)}
            className="text-text-secondary hover:text-text-primary text-2xl"
          >
            ×
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Filename:</label>
            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              className="w-full bg-bg-tertiary border border-border rounded px-3 py-2 text-text-primary"
              placeholder="program.urp"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Robot IP Address:</label>
            <input
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              className="w-full bg-bg-tertiary border border-border rounded px-3 py-2 text-text-primary"
              placeholder="192.168.1.99"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Username:</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-bg-tertiary border border-border rounded px-3 py-2 text-text-primary"
              placeholder="root"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Password:</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-bg-tertiary border border-border rounded px-3 py-2 text-text-primary"
              placeholder="Enter password"
            />
          </div>
          {status && (
            <div className={`text-sm ${status.includes('Error') ? 'text-error' : 'text-info'}`}>
              {status}
            </div>
          )}
          <div className="flex flex-col gap-2 pt-4">
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Upload via SSH'}
            </button>
            <button
              onClick={handleUploadFTP}
              disabled={isUploading}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Upload via FTP'}
            </button>
            <button
              onClick={() => setShowUploadModal(false)}
              className="bg-bg-tertiary text-text-primary px-4 py-2 rounded hover:bg-bg-hover transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

